import { IngredientsContext } from "@/app/clientLayout";
import { AsyncSelectIngredient } from "@/components/AsyncSelectIngredient";
import { InformationCircleComponent } from "@/components/Commons";
import CreateIngredients from "@/components/CreateIngredients";
import DialogComponent from "@/components/Dialog";
import { SortableItem } from "@/components/DragNDrop";
import ErrorMessage from "@/components/ErrorMessage";
import IngredientsGridItem from "@/components/IngredientsGridItem";
import { CurrentUser, UserContext } from "@/components/Page";
import { InventoryGrid } from "@/components/styles/Layout";
import Tooltip from "@/components/styles/Tooltip";
import DragIndicatorIcon from "@/components/svg/DragIndicatorIcon";
import { ingredientToOption } from "@/lib/add-ingredient-utilities";
import {
  MEASUREMENT_ID_WEIGHT,
  combineUnits,
  getUnitOptions,
  searchForUnit,
  stringIsValidNumber,
  stringToNumber,
  unitToOption,
} from "@/lib/measurement";
import useDebounce from "@/lib/useDebounce";
import {
  classNames,
  getIngredientMeasurementDisplay,
  isStudent,
} from "@/lib/util";
import {
  CreateManyIngredientsMutation,
  IngredientComponent,
} from "@/queries/autogenerate/graphql";
import {
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { getIngredientOptions, inlineConversion, unitMap } from "../util";
import { PartialRecipe } from "./FinalisePartialRecipe";

interface Props extends OuterProps {
  user: CurrentUser;
}

const UploadedRecipeIngredient: React.FC<Props> = ({
  user,
  uuid,
  suuid,
  index,
  ingredient,
  setIngredient,
  setAmount,
  setPreparation,
  setUnitId,
  removeIngredient,
}) => {
  const { ingredients: defaultIngredients } = useContext(IngredientsContext);

  const [isCreatingIngredients, setCreatingIngredients] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => {
    setShowDialog(false);
  };

  // For a faster UI response, we use temp variables
  // and debounce the actual update
  const [tempSelected, setTempSelected] = useState(
    ingredient.selected || undefined
  );
  const [tempAmount, setTempAmount] = useState(ingredient.amount || "");
  const [tempPreparation, setTempPreparation] = useState(
    ingredient.preparation || ""
  );
  const [tempUnitId, setTempUnitId] = useState(ingredient.unitId || undefined);

  const debouncedSelected = useDebounce(tempSelected, 1000);
  const debouncedAmount = useDebounce(tempAmount, 1000);
  const debouncedPreparation = useDebounce(tempPreparation, 1000);
  const debouncedUnitId = useDebounce(tempUnitId, 1000);

  useEffect(() => {
    if (ingredient.selected !== debouncedSelected) {
      console.log("ingredient selected changed");
      setIngredient(uuid, suuid, ingredient.uuid, debouncedSelected);
    }
  }, [debouncedSelected]);
  useEffect(() => {
    if (ingredient.amount !== debouncedAmount) {
      console.log("ingredient amount changed");
      setAmount(uuid, suuid, ingredient.uuid, debouncedAmount);
    }
  }, [debouncedAmount]);
  useEffect(() => {
    if (ingredient.preparation !== debouncedPreparation) {
      console.log("ingredient preparation changed");
      setPreparation(uuid, suuid, ingredient.uuid, debouncedPreparation);
    }
  }, [debouncedPreparation]);
  useEffect(() => {
    if (ingredient.unitId !== debouncedUnitId) {
      console.group("ingredient unit changed");
      console.log(ingredient.unitId);
      console.log(debouncedUnitId);
      console.groupEnd();
      setUnitId(uuid, suuid, ingredient.uuid, debouncedUnitId ?? "");
    }
  }, [debouncedUnitId]);

  // const ingredients = optionIngredients
  //   .map((i) => ({
  //     ...i,
  //     similar: true,
  //     similarity: Math.round(
  //       compareTwoStrings(
  //         i.name.toLowerCase(),
  //         (ingredient.name || ingredient.text).toLowerCase()
  //       ) * 100
  //     ),
  //   }))
  //   .sort((a, b) => b.similarity - a.similarity);

  const textInput = useRef<HTMLInputElement>(null);

  const [initialIngredientName, setInitialIngredientName] = useState("");

  const selectedIngredient = defaultIngredients.find(
    (i) => i.id == tempSelected?.id
  );

  // Check if the unit still exists on the ingredient
  const unit = selectedIngredient
    ? combineUnits(selectedIngredient).find((cu) => cu.id === tempUnitId)
    : false;

  console.log(ingredient.uuid);

  useEffect(() => {
    console.log("Mounted UploadedRecipeIngredient");
    return () => console.log("Unmounted UploadedRecipeIngredient");
  }, []);

  return (
    <SortableItem id={ingredient.uuid} index={index} column={suuid}>
      {({ ref, handleRef, isDragging }) => (
        <li
          ref={ref}
          className={classNames(
            "group/ingredient ingredient relative flex rounded-md border-solid bg-white text-left text-sm transition",
            selectedIngredient && unit
              ? "border border-slate-300"
              : "border-2 border-orange-500"
          )}
        >
          <div
            className="flex items-center justify-center px-1.5"
            title="Sort Ingredients"
            ref={handleRef}
          >
            <DragIndicatorIcon className="h-8" style={{ margin: "0" }} />
          </div>
          <div className="flex-1">
            <div className="flex w-full items-center justify-between">
              {ingredient.text ? (
                <small className="my-1.5 text-base font-normal italic">
                  {ingredient.text}
                </small>
              ) : null}
              <Tooltip content="Remove Ingredient">
                <button
                  className="absolute -right-3 -top-3 flex h-9 w-9 scale-50 items-center justify-center rounded-full border border-solid border-slate-300 bg-white opacity-0 transition delay-200 group-hover/ingredient:scale-100 group-hover/ingredient:opacity-100"
                  onClick={() => removeIngredient(uuid, suuid, ingredient.uuid)}
                >
                  <TrashIcon className="icon icon-orange" />
                </button>
              </Tooltip>
            </div>
            <div className="flex w-full items-center">
              <AsyncSelectIngredient
                value={
                  selectedIngredient
                    ? {
                        value: selectedIngredient,
                        label: (
                          <div>
                            {selectedIngredient.name}
                            {selectedIngredient.tags.map((t) => (
                              <div key={t.id} className="tag font-normal">
                                {t.name}
                              </div>
                            ))}
                            <span className="ml-0.5 inline-flex items-center gap-1.5 rounded-md align-middle font-normal">
                              {getIngredientMeasurementDisplay(
                                selectedIngredient
                              )}
                            </span>
                          </div>
                        ),
                        isCreateIngredient: false,
                      }
                    : null
                }
                externalRef={textInput}
                placeholder="Select the best matching ingredient"
                // isClearable
                getDefaultOptions={(ingredients) =>
                  getIngredientOptions(
                    ingredient.name || ingredient.text,
                    ingredients
                  ).map(ingredientToOption)
                }
                // cacheOptions
                // loadOptions={loadOptions}
                onChange={(value) => {
                  if (value) {
                    setTempSelected(value);

                    const tempIngredient = defaultIngredients.find(
                      (i) => i.id === value.id
                    );

                    if (tempIngredient) {
                      let unitText = "";

                      const amountRegEx = /^[0-9. ]+ *\w+/g;
                      const amountTextMatch =
                        ingredient.text?.match(amountRegEx);
                      const amountText = amountTextMatch
                        ? amountTextMatch[0]
                        : "";

                      const unitSubstring = amountText.match(/[A-z]+/g);
                      const str = unitSubstring && unitSubstring[0];
                      if (str && unitMap.hasOwnProperty(str.toLowerCase())) {
                        unitText =
                          unitMap[str === "T" ? str : str.toLowerCase()];
                      }

                      setTempUnitId(
                        searchForUnit(
                          combineUnits(tempIngredient),
                          unitText,
                          tempIngredient.density ?? undefined,
                          tempIngredient.selectionUnit ?? undefined
                        ).id
                      );
                    }
                  } else {
                    setTempSelected(undefined);
                  }
                }}
                onCreated={(ingredients) => {
                  const tempIngredient = ingredients[0];
                  setTempSelected(tempIngredient);

                  let unitText = "";

                  const amountRegEx = /^[0-9. ]+ *\w+/g;
                  const amountTextMatch = ingredient.text?.match(amountRegEx);
                  const amountText = amountTextMatch ? amountTextMatch[0] : "";

                  const unitSubstring = amountText.match(/[A-z]+/g);
                  const str = unitSubstring && unitSubstring[0];
                  if (str && unitMap.hasOwnProperty(str.toLowerCase())) {
                    unitText = unitMap[str === "T" ? str : str.toLowerCase()];
                  }

                  setTempUnitId(
                    searchForUnit(
                      combineUnits(tempIngredient),
                      unitText,
                      tempIngredient.density ?? undefined,
                      tempIngredient.selectionUnit ?? undefined
                    ).id
                  );
                }}
                className="flex-1"
                styles={{
                  container: (provided) => ({
                    ...provided,
                    // width: "300px",
                    display: "inline-block",
                  }),
                }}
              />
              {selectedIngredient ? (
                <>
                  <Tooltip content="Edit Ingredient">
                    <button className="visible mx-0.5" onClick={open}>
                      <PencilIcon className="icon icon-gray opacity-0 group-hover/ingredient:opacity-100" />
                    </button>
                  </Tooltip>
                  <DialogComponent
                    isOpen={showDialog}
                    onClose={close}
                    aria-label="Edit ingredient"
                  >
                    <h2 className="mb-8">Edit {selectedIngredient.name}</h2>
                    <IngredientComponent
                      variables={{ where: { id: selectedIngredient.id } }}
                    >
                      {({ error, fetching, data }) =>
                        error ? (
                          <ErrorMessage error={error} />
                        ) : fetching ? (
                          <div className="spinner" />
                        ) : data?.ingredient ? (
                          <InventoryGrid itemHeight="90px" itemWidth="400px">
                            <IngredientsGridItem
                              user={user}
                              ingredient={data.ingredient}
                              isFragment={true}
                            />
                          </InventoryGrid>
                        ) : (
                          <p>No Data! (816)</p>
                        )
                      }
                    </IngredientComponent>
                  </DialogComponent>
                </>
              ) : (
                <span className="mx-1.5 w-5" />
              )}
            </div>
            {selectedIngredient ? (
              <>
                <div className="mb-2 mr-9 mt-4 flex items-center gap-1.5">
                  <label htmlFor={`amount${uuid}-${ingredient.uuid}`}>
                    <span className="font-normal">Amount</span>
                    <div className="relative">
                      <input
                        type="text"
                        id={`amount${uuid}-${ingredient.uuid}`}
                        value={tempAmount ?? ""}
                        onChange={(e) => setTempAmount(e.target.value)}
                        className={classNames(
                          "input w-22",
                          stringIsValidNumber(String(tempAmount))
                            ? ""
                            : "border-orange-400 outline-orange-600"
                        )}
                      />
                      {stringIsValidNumber(String(tempAmount)) ? null : (
                        <Tooltip
                          content={`"${tempAmount}" is not a valid input`}
                        >
                          <span>
                            <ExclamationTriangleIcon className="icon icon-orange absolute right-0 top-0 mt-3.5" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </label>
                  <label htmlFor={`unit${uuid}-${ingredient.uuid}`}>
                    <span className="font-normal">Unit</span>
                    <Select
                      id={`unit-${selectedIngredient.id}`}
                      classNamePrefix="react-select"
                      value={unit ? unitToOption(unit) : null}
                      options={getUnitOptions(
                        combineUnits(selectedIngredient),
                        selectedIngredient.density ?? undefined
                      )}
                      noOptionsMessage={() => "No Options"}
                      onChange={(data) =>
                        setTempUnitId(
                          combineUnits(selectedIngredient).find(
                            (u) => u.id === data?.value
                          )?.id ?? undefined
                        )
                      }
                      styles={{
                        container: (provided) => ({
                          ...provided,
                          width: "175px",
                        }),
                      }}
                    />
                  </label>
                  {!unit ? (
                    <Tooltip
                      content={
                        "The selected unit does not exist on this ingredient. Please change the unit."
                      }
                    >
                      <span>
                        <ExclamationTriangleIcon className="icon icon-orange" />
                      </span>
                    </Tooltip>
                  ) : null}
                  <label
                    htmlFor={`preparation${uuid}-${ingredient.uuid}`}
                    className="flex-1"
                  >
                    <span className="font-normal">Preparation Method</span>
                    <input
                      type="text"
                      id={`preparation${uuid}-${ingredient.uuid}`}
                      className="input w-full"
                      placeholder="grated, sliced..."
                      value={tempPreparation}
                      onChange={(e) => setTempPreparation(e.target.value)}
                    />
                  </label>
                </div>
                {(unit &&
                  typeof tempAmount === "string" &&
                  tempAmount.includes("/")) ||
                (unit &&
                  ((selectedIngredient.measurement?.id ===
                    MEASUREMENT_ID_WEIGHT &&
                    selectedIngredient.density) ||
                    (!unit.measurementId &&
                      selectedIngredient.measurement))) ? (
                  <div className="my-2.5 flex items-center gap-1.5">
                    {unit &&
                    ((selectedIngredient.measurement?.id ===
                      MEASUREMENT_ID_WEIGHT &&
                      selectedIngredient.density) ||
                      (!unit.measurementId &&
                        selectedIngredient.measurement)) ? (
                      <div className="pl-1.5">
                        {inlineConversion(
                          tempAmount,
                          unit,
                          combineUnits(selectedIngredient),
                          selectedIngredient?.density ?? 0
                        )}
                        <Tooltip
                          content="Conversions are shown inline for a quick reference check. If the conversion is incorrect, this means the density is incorrect and should be updated."
                          placement="bottom"
                        >
                          <span>
                            <InformationCircleComponent />
                          </span>
                        </Tooltip>
                      </div>
                    ) : (
                      <span className="">
                        {stringToNumber(String(tempAmount))}
                      </span>
                    )}
                  </div>
                ) : null}
              </>
            ) : isStudent(user) &&
              !user.school?.permCreateIngredients ? null : (
              <div
                className={classNames(
                  "createNewIngredient",
                  isCreatingIngredients
                    ? "my-2.5 mr-8 rounded border border-solid border-cyan-200 bg-gradient-to-tr from-cyan-50 to-green-50 p-2.5"
                    : ""
                )}
              >
                <button
                  onClick={() => setCreatingIngredients(!isCreatingIngredients)}
                  className={isCreatingIngredients ? "btn btn-warning" : "btn"}
                >
                  {isCreatingIngredients ? "Cancel" : "Or Create a New One"}
                </button>
                {isCreatingIngredients && user.school ? (
                  <CreateIngredients
                    school={user.school}
                    name={initialIngredientName}
                    onCompleted={(
                      ingredients?: CreateManyIngredientsMutation["createManyIngredients"]
                    ) => {
                      // remove the create ingredients section
                      setInitialIngredientName("");
                      setCreatingIngredients(false);

                      if (textInput.current != document.activeElement) {
                        textInput.current?.focus();
                      }

                      if (ingredients?.length) {
                        const tempIngredient = ingredients[0];
                        setTempSelected(tempIngredient);

                        // TODO: extract this into a function as it's used in multiple places
                        if (tempIngredient && tempIngredient) {
                          let unitText = "";

                          const amountRegEx = /^[0-9. ]+ *\w+/g;
                          const amountTextMatch =
                            ingredient.text.match(amountRegEx);
                          const amountText = amountTextMatch
                            ? amountTextMatch[0]
                            : "";

                          const unitSubstring = amountText.match(/[A-z]+/g);
                          const str = unitSubstring && unitSubstring[0];
                          if (
                            str &&
                            unitMap.hasOwnProperty(str.toLowerCase())
                          ) {
                            unitText =
                              unitMap[str === "T" ? str : str.toLowerCase()];
                          }

                          setTempUnitId(
                            searchForUnit(
                              combineUnits(tempIngredient),
                              unitText,
                              tempIngredient.density ?? undefined,
                              tempIngredient.selectionUnit ?? undefined
                            ).id
                          );
                        }
                      }

                      // // Let the user know: created but will take a while to show up in search
                      // toast(
                      //   "The created ingredients may take a few seconds to show up in search results."
                      // );
                    }}
                  />
                ) : null}
              </div>
            )}
          </div>
        </li>
      )}
    </SortableItem>
  );
};

interface OuterProps {
  uuid: string;
  suuid: string; // section uuid
  index: number;
  ingredient: PartialRecipe["ingredientSections"][number]["ingredients"][number];
  setIngredient: (
    id: string,
    suuid: string,
    sub: string,
    newSelected: PartialRecipe["ingredientSections"][number]["ingredients"][number]["selected"]
  ) => void;
  setAmount: (
    id: string,
    suuid: string,
    sub: string,
    value: string | number
  ) => void;
  setPreparation: (
    id: string,
    suuid: string,
    sub: string,
    value: string
  ) => void;
  setUnitId: (id: string, suuid: string, sub: string, value: string) => void;
  removeIngredient: (id: string, suuid: string, sub: string) => void;
}

const Outer: React.FC<OuterProps> = (props) => {
  return (
    <UserContext.Consumer>
      {(user) => <UploadedRecipeIngredient {...props} user={user} />}
    </UserContext.Consumer>
  );
};

export default Outer;
